import {Duration} from 'luxon';

import cfg from 'client/common/utils/client-config';

import {VideoService} from 'client/common/types';

const REGEXPS: {[service in VideoService]?: RegExp} = {
    // https://www.youtube.com/watch?v=WijF8aivOo8
    youtube: /(?:^.*youtube\.com\/\S*(?:(?:\/e(?:mbed))?\/|watch\?(?:\S*?&?v=))|youtu\.be\/)([a-zA-Z0-9_-]{6,11})/,
    // https://yandex.ru/efir?stream_id=vADvTcaLx4Nk&from=efir&from_block=videohub
    efir: /(?:^.*yandex.ru\/efir.*?stream_id=)([^#&?]+)/,
    // https://frontend.vh.yandex.ru/player/vADvTcaLx4Nk?from=partner&mute=1&autoplay=1&branding=0
    vh: /(?:^.*frontend.vh.yandex.ru\/player\/)([^#&?]+)/
};

interface VideoData {
    id: string;
    service: VideoService;
}

// TODO: Модульные тесты
export function parseVideoLink(url: string): VideoData | undefined {
    for (const service of ['youtube', 'efir', 'vh'] as VideoService[]) {
        const match = REGEXPS[service]?.exec(url);

        const id = match && match[1];

        if (!id) {
            continue;
        }

        return {service, id};
    }
}

export function getVideoLink(service: VideoService, id: string): string | undefined {
    if (!id) {
        return;
    }

    switch (service) {
        case 'youtube':
            return `https://www.youtube.com/watch?v=${id}`;

        case 'efir':
            return `https://yandex.ru/efir?stream_id=${id}`;

        case 'vh':
            return `https://frontend.vh.yandex.ru/player/${id}`;
        case 'resource':
            return `${cfg.resourceVideoOrigin}/player/${id}`;
    }
}

export async function getPreviewLink(service: VideoService, id: string): Promise<string> {
    switch (service) {
        case 'youtube':
            return `https://img.youtube.com/vi/${id}/0.jpg`;

        case 'efir':
        case 'vh': {
            const res = await fetch(`https://frontend.vh.yandex.ru/player/${id}.json?from=pythia`);
            const body = await res.json();

            return body?.content?.thumbnail;
        }
        case 'resource': {
            const res = await fetch(`${cfg.resourceVideoOrigin}/player/${id}.json?from=pythia`);
            const body = await res.json();

            return body?.content?.thumbnail;
        }
    }
}

export function getEmbedLink(service: VideoService, id: string): string {
    switch (service) {
        case 'youtube':
            return `https://www.youtube.com/embed/${id}`;

        case 'efir':
        case 'vh':
            return `https://frontend.vh.yandex.ru/player/${id}?autoplay=0`;
        case 'resource':
            return `${cfg.resourceVideoOrigin}/player/${id}?autoplay=0`;
    }
}

export interface VideoInfo {
    id: string;
    service: VideoService;
    duration: number;
    title: string;
}

export type VideoErrorReason =
    | 'UNSUPPORTED_SERVICE'
    | 'UPLOAD_ERROR'
    | 'NOT_FOUND'
    | 'DURATION_TOO_LONG'
    | 'SIZE_TOO_BIG';

export type GetVideoInfoResult =
    | VideoInfo
    | {
          error?: {
              reason: VideoErrorReason;
          };
      };

function getVideoDataEndpoint(service: VideoService, id: string) {
    switch (service) {
        case 'youtube':
            // eslint-disable-next-line max-len
            return `https://youtube.googleapis.com/youtube/v3/videos?part=snippet&part=contentDetails&id=${id}&key=${cfg.youtubeApiKey}`;
        case 'efir':
        case 'vh':
            return `https://frontend.vh.yandex.ru/player/${id}.json`;
        default:
            return;
    }
}

function parseVideoResponse(service: VideoService, data: any) {
    if (data?.error) {
        return {
            error: data.error
        };
    }

    let result;
    let content;

    switch (service) {
        case 'youtube':
            content = data?.items?.[0] ?? {};

            result = {
                service,
                id: content?.id,
                title: content?.snippet?.title,
                duration: Duration.fromISO(content?.contentDetails?.duration).as('seconds')
            };
            break;
        case 'vh':
        case 'efir':
            content = data?.content;

            result = {
                id: content.content_id,
                service,
                title: content?.title,
                duration: content?.duration
            };
            break;
        default:
            return {
                error: {
                    reason: 'UNSUPPORTED_SERVICE'
                }
            };
    }

    if (!result?.title) {
        return {
            error: {
                reason: 'NOT_FOUND'
            }
        };
    }

    if (result.duration > cfg.creative.maxDuration) {
        return {
            error: {
                reason: 'DURATION_TOO_LONG'
            }
        };
    }

    return result;
}

export async function getVideoInfo(service: VideoService, id: string): Promise<GetVideoInfoResult> {
    const endpoint = getVideoDataEndpoint(service, id);

    if (!endpoint) {
        return {
            error: {
                reason: 'UNSUPPORTED_SERVICE'
            }
        };
    }

    const res = await fetch(endpoint);

    if (res.status !== 200) {
        return {
            error: {
                reason: 'UPLOAD_ERROR'
            }
        };
    }

    const body = await res.json();

    return parseVideoResponse(service, body);
}
